import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { useAuth, roleHome } from "../context/AuthContext";
import { Brand } from "../components/Brand";
import { Check, ArrowRight, X, Loader2, Building2, User, Sparkles } from "lucide-react";
import { toast } from "sonner";

function ContactModal({ onClose }) {
  const [f, setF] = useState({ name: "", email: "", college: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.name || !f.email) return toast.error("Name and email required");
    setBusy(true);
    try { const { data } = await api.post("/billing/contact-sales", f); toast.success(data.message); onClose(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-ink/50" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-lift w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-1"><div className="font-heading font-bold text-lg text-ink">Contact Sales</div><button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button></div>
        <p className="text-xs text-ink-soft mb-4">Enterprise details are shared with <span className="font-semibold">sales@mock2crack.ai</span></p>
        <div className="space-y-3">
          <input data-testid="contact-name" placeholder="Your name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
          <input data-testid="contact-email" placeholder="Work email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
          <input placeholder="College / Institution" value={f.college} onChange={(e) => setF({ ...f, college: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
          <textarea placeholder="How can we help?" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} rows={3} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
        </div>
        <button data-testid="contact-submit" onClick={submit} disabled={busy} className="mt-4 w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3 rounded-full disabled:opacity-60 transition-colors">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request a call"}</button>
      </div>
    </div>
  );
}

function PlanCard({ plan, cur, cta, popular, children }) {
  return (
    <div className={`rounded-2xl p-7 border relative flex flex-col ${popular ? "border-brand bg-slate-900 text-white shadow-ambient" : "border-slate-200 bg-white"}`}>
      {popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-wider bg-brand text-white px-3 py-1 rounded-full">Most Popular</span>}
      <div className={`text-sm font-bold ${popular ? "text-white" : "text-ink"}`}>{plan.name}</div>
      <div className={`text-xs mb-4 ${popular ? "text-blue-200" : "text-ink-faint"}`}>{plan.tagline}</div>
      <div className="font-heading text-3xl font-extrabold num mb-1">
        {plan.price === 0 ? "Free" : plan.price == null ? (plan.type === "payg" ? `${cur}${plan.payg_prices.ai}+` : "Custom") : `${cur}${plan.price.toLocaleString()}`}
      </div>
      {plan.period && plan.price ? <div className={`text-xs mb-5 ${popular ? "text-blue-200" : "text-ink-faint"}`}>/ {plan.period}</div> : <div className="mb-5" />}
      <ul className="space-y-2.5 mb-6 flex-1">
        {plan.features.map((f) => (
          <li key={f} className={`flex items-start gap-2.5 text-sm ${popular ? "text-blue-50" : "text-ink-soft"}`}><Check className={`h-4 w-4 mt-0.5 shrink-0 ${popular ? "text-white" : "text-emerald-500"}`} /> {f}</li>
        ))}
      </ul>
      {children || cta}
    </div>
  );
}

export default function Pricing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("individual");
  const [contact, setContact] = useState(false);

  useEffect(() => { api.get("/billing/plans").then((r) => setD(r.data)); }, []);
  if (!d) return <div className="min-h-screen grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>;

  const go = (kind) => { if (!user) { navigate("/login"); return; } navigate(`/checkout?kind=${kind}`); };
  const cur = d.currency;
  const Btn = ({ testid, onClick, label, dark }) => (
    <button data-testid={testid} onClick={onClick} className={`w-full text-sm font-bold px-5 py-3 rounded-full transition-colors ${dark ? "bg-white text-brand hover:bg-blue-50" : "bg-brand text-white hover:bg-brand-hover"}`}>{label}</button>
  );

  return (
    <div className="min-h-screen bg-background">
      {contact && <ContactModal onClose={() => setContact(false)} />}
      <header className="glass border-b border-slate-200 px-5 h-16 flex items-center justify-between sticky top-0 z-30">
        <Brand />
        <div className="flex items-center gap-3">
          {user ? <button onClick={() => navigate(roleHome(user.role))} className="text-sm font-bold text-white bg-brand px-5 py-2.5 rounded-full">Dashboard</button>
            : <button onClick={() => navigate("/login")} className="text-sm font-bold text-white bg-brand px-5 py-2.5 rounded-full">Login</button>}
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 py-14 fade-up">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 text-xs font-semibold text-brand bg-brand-light px-3 py-1.5 rounded-full mb-4"><Sparkles className="h-3.5 w-3.5" /> Simple, transparent pricing</div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-ink">Invest in your Einstein Score</h1>
          <p className="text-ink-soft mt-3">Choose an individual plan or empower your entire institution.</p>
        </div>

        <div className="flex justify-center mt-8 mb-10">
          <div className="flex gap-1 bg-slate-100 rounded-full p-1">
            <button data-testid="tab-individual" onClick={() => setTab("individual")} className={`inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-full transition-colors ${tab === "individual" ? "bg-white text-ink shadow-sm" : "text-ink-soft"}`}><User className="h-4 w-4" /> Individual</button>
            <button data-testid="tab-college" onClick={() => setTab("college")} className={`inline-flex items-center gap-2 text-sm font-bold px-5 py-2.5 rounded-full transition-colors ${tab === "college" ? "bg-white text-ink shadow-sm" : "text-ink-soft"}`}><Building2 className="h-4 w-4" /> For Colleges</button>
          </div>
        </div>

        {tab === "individual" ? (
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {d.individual_plans.map((p) => (
              <PlanCard key={p.id} plan={p} cur={cur} popular={p.popular}>
                {p.id === "free" && <Btn testid="cta-free" onClick={() => go("free") || navigate(user ? roleHome(user.role) : "/login")} label={user ? "Go to Dashboard" : "Start Free"} />}
                {p.id === "payg" && (
                  <div className="space-y-2">
                    <Btn testid="cta-payg-ai" onClick={() => go("payg_ai")} label={`Buy AI Interview · ${cur}${p.payg_prices.ai}`} />
                    <button data-testid="cta-payg-expert" onClick={() => go("payg_expert")} className="w-full text-sm font-bold px-5 py-3 rounded-full bg-slate-100 text-ink hover:bg-slate-200 transition-colors">Buy Expert · {cur}{p.payg_prices.expert}</button>
                  </div>
                )}
                {p.id === "pro" && <Btn testid="cta-pro" dark onClick={() => go("pro")} label="Upgrade to Pro" />}
              </PlanCard>
            ))}
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 items-start">
            {d.college_plans.map((p) => (
              <PlanCard key={p.id} plan={p} cur={cur} popular={p.popular}>
                {p.type === "contact"
                  ? <Btn testid="cta-enterprise" onClick={() => setContact(true)} label="Contact Us" />
                  : <Btn testid={`cta-${p.id}`} dark={p.popular} onClick={() => go(`college_${p.id}`)} label={`Get ${p.credits} credits`} />}
              </PlanCard>
            ))}
          </div>
        )}

        <p className="text-center text-xs text-ink-faint mt-8">Payments are simulated in this demo. Razorpay / Stripe can be integrated later. All prices configurable by Platform Admin.</p>
      </div>
    </div>
  );
}
